import {useQuery} from '@tanstack/react-query';
import {fetchRecords as fetchInvoices} from '../../../invoices/services/InvoicesService';
import {fetchRecords as fetchSubscriptions} from '../../../customer-subscriptions/services/CustomerSubscriptionsService';
import {fetchRecords as fetchPaymentProfiles} from '../../../payment-profiles/services/PaymentProfilesService';
import {fetchRecords as fetchOrders} from '../../../orders/services/OrdersService';
import {CustomerSubscription, CustomerSubscriptionStatus} from '../../../customer-subscriptions/types';

const calcSubscriptionValue = (subscriptions: CustomerSubscription[]) => {
    return subscriptions
        .filter(s => s.status === CustomerSubscriptionStatus.Active)
        .reduce((total, sub) => {
            const itemsTotal = sub.items?.items?.reduce((sum, item) => {
                const amount = (item.price as { amount?: number })?.amount ?? 0;
                return sum + (amount * item.quantity);
            }, 0) ?? 0;
            return total + itemsTotal;
        }, 0);
};

export const useCustomerOverview = (customerId: string) => {
    const invoicesQuery = useQuery({
        queryKey: ['customer-overview-invoices', customerId],
        queryFn: () => fetchInvoices({
            filter: {customerId},
            limit: 5,
            sort: {column: 'created', direction: 'desc'},
        }),
        enabled: !!customerId,
    });

    const subscriptionsQuery = useQuery({
        queryKey: ['customer-overview-subscriptions', customerId],
        queryFn: () => fetchSubscriptions({
            filter: {customerId},
            limit: 100,
            sort: {column: 'created', direction: 'desc'},
        }),
        enabled: !!customerId,
    });

    const paymentProfilesQuery = useQuery({
        queryKey: ['customer-overview-payment-profiles', customerId],
        queryFn: () => fetchPaymentProfiles({
            filter: {customerId},
            limit: 10,
            sort: {column: 'created', direction: 'desc'},
        }),
        enabled: !!customerId,
    });

    const ordersQuery = useQuery({
        queryKey: ['customer-overview-orders', customerId],
        queryFn: () => fetchOrders({
            filter: {customerId},
            limit: 5,
            sort: {column: 'created', direction: 'desc'},
        }),
        enabled: !!customerId,
    });

    const invoices = invoicesQuery.data?.items ?? [];
    const subscriptions = (subscriptionsQuery.data?.items ?? []) as CustomerSubscription[];
    const paymentProfiles = paymentProfilesQuery.data?.items ?? [];
    const orders = ordersQuery.data?.items ?? [];

    const activeSubscriptions = subscriptions.filter(s => s.status === CustomerSubscriptionStatus.Active);
    const activePaymentProfiles = paymentProfiles.filter((p: { status: string }) => p.status === 'Active');

    return {
        invoices: {
            items: invoices,
            totalCount: invoices.length,
            latestInvoice: invoices[0] ?? null,
            loading: invoicesQuery.isLoading,
        },
        subscriptions: {
            items: subscriptions,
            activeCount: activeSubscriptions.length,
            totalCount: subscriptions.length,
            totalRecurringValue: calcSubscriptionValue(subscriptions),
            loading: subscriptionsQuery.isLoading,
        },
        paymentProfiles: {
            items: paymentProfiles,
            activeCount: activePaymentProfiles.length,
            totalCount: paymentProfiles.length,
            hasActiveProfile: activePaymentProfiles.length > 0,
            loading: paymentProfilesQuery.isLoading,
        },
        orders: {
            items: orders,
            totalCount: orders.length,
            latestOrder: orders[0] ?? null,
            loading: ordersQuery.isLoading,
        },
        loading: invoicesQuery.isLoading || subscriptionsQuery.isLoading || paymentProfilesQuery.isLoading || ordersQuery.isLoading,
    };
};
